// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Forwards the existing useToast()/toast() API to sonner, which is the toast
// renderer actually mounted (App.tsx). The Radix renderer
// (components/ui/toaster.tsx) has no importers, so everything dispatched
// through the old in-memory store was invisible to users.
//
// No call-site changes required: callers pass only title / description /
// variant / duration, all of which sonner supports.
import type { MouseEvent, ReactNode } from "react";
import { toast as sonnerToast } from "sonner";

type ToastInput = {
  title?: ReactNode;
  description?: ReactNode;
  variant?: "default" | "destructive" | null;
  duration?: number;
  action?: { label: ReactNode; onClick: (event: MouseEvent) => void };
};

type SonnerId = string | number;

function show(
  { title, description, variant, duration, action }: ToastInput,
  id?: SonnerId,
): SonnerId {
  // Callers overwhelmingly pass title + description; a few pass only one.
  const message = title ?? description ?? "";
  const options = {
    description: title ? description : undefined,
    duration,
    action,
    ...(id !== undefined ? { id } : {}),
  };

  return variant === "destructive"
    ? sonnerToast.error(message, options)
    : sonnerToast(message, options);
}

function toast(props: ToastInput = {}) {
  const id = show(props);

  return {
    id: String(id),
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: ToastInput) => show({ ...props, ...next }, id),
  };
}

function useToast() {
  return {
    // The Radix renderer was the only reader of this array; it is unmounted.
    toasts: [] as never[],
    toast,
    dismiss: (toastId?: string) => sonnerToast.dismiss(toastId),
  };
}

export { useToast, toast };
